import { boot } from "quasar/wrappers";
import axios from "axios";
import { throttle, LocalStorage, Platform } from "quasar";
import { usePersistedStore } from "src/stores/persisted-store";
import { useCommonStore } from "src/stores/common-store";
import { oidc } from "./oidc";
import {
  API_ENDPOINTS,
  IDENTITY_API_ENDPOINTS,
} from "src/constants/api-endpoints";
import {
  validateJWTToken,
  reloadPage,
  sendMessageToParentApp,
} from "src/utils";
import { requestParentTokenRefresh } from "src/utils/parent-token-refresh";
// import { reloadPage } from "src/utils";
import { IFrameActions } from "src/constants";
import { getAccountID } from "src/utils/commonFunction";

export var objRequests = {
  Accept: "application/json",
  "Content-Type": "application/json",
};
export var routeName = null;

// Be careful when using SSR for cross-request state pollution
// due to creating a Singleton instance here;
// If any client changes this (global) instance, it might be a
// good idea to move this instance creation inside of the
// "export default () => {}" function below (which runs individually
// for each client)
const commonReqHeader = {
  Accept: "application/json",
  "Content-Type": "application/json",
};

const tenantReqHeader = { ...commonReqHeader };

const account_id = process.env.ACCOUNT_ID || null;
if (
  account_id &&
  (Platform?.is?.cordova || process.env.APP_ENV == "development")
) {
  tenantReqHeader["FTX-BS"] = account_id;
}

export const axiosInstance = axios.create({
  baseURL: process.env.API_ROOT_URI,
  withCredentials: false, // This is the default
  timeout: 1000 * 90,
  headers: {
    ...tenantReqHeader,
  },
});

export const identityAxiosInstance = axios.create({
  baseURL: process.env.IDENTITY_API_ROOT_URI,
  withCredentials: false, // This is the default
  headers: {
    ...commonReqHeader,
  },
});

export const eaivAxiosInstance = axios.create({
  baseURL: process.env.EAIV_FTXIdentity_API_ROOT_URI,
  withCredentials: false, // This is the default
  headers: {
    ...commonReqHeader,
  },
});

export default boot(({ app }) => {
  app.config.globalProperties.$request = axiosInstance;
  app.config.globalProperties.apiEndpoints = API_ENDPOINTS;
  app.config.globalProperties.identityApiEndpoints = IDENTITY_API_ENDPOINTS;
  app.config.globalProperties.$identityRequest = identityAxiosInstance;
  app.config.globalProperties.$eaivRequest = eaivAxiosInstance;

  //TODO :- replace this with dynamic token generation
  // for use inside Vue files (Options API) through this.$axios and this.$api

  const persistedStore = usePersistedStore();
  const commonStore = useCommonStore();

  axiosInstance.interceptors.request.use(
    async function (config) {
      //validate token before request
      if (
        persistedStore.isLoggedIn &&
        !validateJWTToken(persistedStore.user.access_token)
      ) {
        await persistedStore.mapUser();
        config.headers[
          "Authorization"
        ] = `${persistedStore?.user?.token_type} ${persistedStore?.user?.access_token}`;
      }
      try {
        const hasAccAlready = (config) => {
          // check in url (GET case)
          if (config.url?.includes("acc=")) return true;

          //check in url (Get case isDashboard)
          if (config.url?.includes("isDashboard")) return true;

          if (config.url?.includes("doNotPassedAcc")) return true;

          //check in url if type=mfg is present, then we should not add acc in url as it is used for manufacturer search and it uses store id instead of account id
          if (config.url?.includes("type=mfg")) return true;

          // check in params
          if (config.params?.acc) return true;

          // check in body
          if (config.data?.acc) return true;

          return false;
        };
        //------ Here we pass acc to every request
        const addAccountID = (url, id) =>
          url.includes("?") ? `${url}&acc=${id}` : `${url}?acc=${id}`;
        const accExclusionRoutes = [
          ...[
            "customer/news-updates",
            "customer/auth/store-configuration",
            "customer/stores/my-stores",
            "customer/poll-update",
            "customer/full-search",
            "customer/campaigns/hot-campaigns",
            "customer/profile-update",
            "customer/messages",
            "customer/associate-reg-token",
            "customer/manufacturers",
            "customer/rank-info",
            "add-friend",
            "/block",
            "/unblock",
            "friend-requests",
            "customer/friend-list/",
            "customer/campaigns/customer",
            "customer/stores/by-customer",
            "customer/earning-history?customer_id",
            "/my-allowed-stores",
            "customer/friend-list",
            "customer/stores/get-distance-filter-list",
            "customer/stores/available",
            "customer/avatars",
            "/send-team-request",
            "/customer/team-requests/",
            "customer/addresses",
            "customer/add-feedback",
            "customer/get-consent-topics",
            "customer/get-exit-questions",
            "customer/save-exit-questions",
            "customer/delete-account",
            "customer/manufacturer/swag-shop/products",
            "customer/team-requests",
            "customer/manufacturer/gifts",
            "customer/manufacturer/swag-shop/purchases",
            "customer/manufacturer/balances",
            "customer/campaigns/dashboard/campaigns",
            "customer/save-consent-preferences",
          ],
          !getAccountID() && "customer/details",
        ];
        const isExcluded = accExclusionRoutes.some((route) =>
          config.url.includes(route)
        );
        if (!isExcluded && !hasAccAlready(config)) {
          if (getAccountID()) {
            const id = getAccountID();

            if (config.method.toLowerCase() === "get") {
              config.url = addAccountID(config.url, id);
            } else if (config.method.toLowerCase() === "post") {
              config.data
                ? (config.data.acc = id)
                : config.params
                ? (config.params.acc = id)
                : (config.params = { acc: id });
            }
          } else if (
            !commonStore.appInIframe &&
            commonStore?.selectedStoreIDForeSearch?.length > 0
          ) {
            const id = commonStore.selectedStoreIDForeSearch;
            if (config.method.toLowerCase() === "get") {
              if (!config.url.includes("type=mfg")) {
                config.url = addAccountID(config.url, id);
              }
            } else if (config.method.toLowerCase() === "post") {
              config.data
                ? (config.data.acc = id)
                : config.params
                ? (config.params.acc = id)
                : (config.params = { acc: id });
            }
          }
        }
      } catch (error) {
        console.log("error", error);
      }

      //////----------------------------------------------------------------
      if (!routeName || config.method.toLocaleLowerCase() !== "get") {
        return config;
      }
      let axiosSource = createAxiosCancelSource();
      config.cancelToken = axiosSource.token;

      let pageIndex = (config.params && config.params.page) || 0,
        oldParams = null;

      if (objRequests[config.url]) {
        if (
          objRequests[config.url][pageIndex] &&
          objRequests[config.url][pageIndex].params
        ) {
          oldParams = objRequests[config.url][pageIndex].params;
        } else {
          for (let page in objRequests[config.url]) {
            if (objRequests[config.url][page].params) {
              oldParams = objRequests[config.url][page].params;
              break;
            }
          }
        }
        if (oldParams !== null) {
          let blnIsSameRequest = isEqualObject(config.params || {}, oldParams);

          if (blnIsSameRequest) {
            if (
              objRequests[config.url][pageIndex] &&
              objRequests[config.url][pageIndex].cancel
            ) {
              objRequests[config.url][pageIndex].cancel();
            }
          } else {
            for (let page in objRequests[config.url]) {
              if (objRequests[config.url][page].cancel) {
                oldParams = objRequests[config.url][page].cancel();
                delete objRequests[config.url][page];
              }
            }
          }
        }
      }

      if (!objRequests[config.url]) {
        objRequests[config.url] = {};
      }

      objRequests[config.url][pageIndex] = {
        cancel: axiosSource.cancel,
        params: config.params || {},
      };

      return config;
    },
    function (error) {}
  );

  const handleTokenExpire = throttle((error) => {
    // if (error.config._retry) return reloadPage();
    // error.config._retry = true;
    return oidc
      ?.getUser()
      .then(async (user) => {
        if (user && user.access_token) {
          try {
            const config = error.config;

            let data = await oidc.signinSilent();
            setAxiosHeaders(data?.access_token, data?.token_type);
            config.headers[
              "Authorization"
            ] = `${data?.token_type} ${data?.access_token}`;

            persistedStore.mapUser();

            return new Promise((resolve, reject) => {
              axiosInstance
                .request(config)
                .then((response) => {
                  resolve(response);
                })
                .catch((error) => {
                  reject(error);
                });
            });
          } catch (error) {
            await oidc?.removeUser();
            persistedStore.logout();
            return Promise.resolve();
          }
        } else {
          // return refreshClientCred(error)
          persistedStore.logout();
          return Promise.resolve();
        }
      })
      .catch((err) => {
        // return refreshClientCred(error)
        // console.log("handleTokenExpire Error", err);
        persistedStore.logout();
        return Promise.resolve();
      });
  }, 5000);

  /**
   * 401 while embedded in the commerce app.
   *
   * The host owns the OIDC session, so instead of reloading the page we ask it
   * to silently renew, adopt the token it sends back and replay the failed
   * request. Only one attempt: a 401 on a token the host just minted means the
   * session is genuinely gone, and we fall back to reloading the host.
   */
  const retryWithHostRefreshedToken = async (error) => {
    const config = error?.config;

    if (!config || config._hostTokenRetry) {
      sendMessageToParentApp({ action: IFrameActions.REFRESH });
      return Promise.reject(error);
    }

    config._hostTokenRetry = true;

    const refreshedUser = await requestParentTokenRefresh();

    if (!refreshedUser?.access_token) {
      sendMessageToParentApp({ action: IFrameActions.REFRESH });
      return Promise.reject(error);
    }

    // Stores the user and puts the new token on the axios defaults, so calls
    // made after this one are already authorised.
    persistedStore.addUser(refreshedUser);

    config.headers = config.headers || {};
    config.headers["Authorization"] = `${
      refreshedUser.token_type || "Bearer"
    } ${refreshedUser.access_token}`;

    return axiosInstance.request(config);
  };

  axiosInstance.interceptors.response.use(
    (response) => {
      if (response && response.config) clearRequest(response.config);
      return response;
    },
    (error) => {
      if (isCancelRequest(error)) {
        if (error.response && error.response.config)
          clearRequest(error.response.config);

        return null;
      }

      if (
        error?.response?.status === 401 &&
        ["DUMP_EXCEPTION", "AUTH_TOKEN_EXPIRED"].includes(
          error?.response?.data?.errorCode
        )
      ) {
        if (commonStore.appInIframe) {
          return retryWithHostRefreshedToken(error);
        } else {
          LocalStorage.set("lns-error-log", JSON.stringify(error.response));
          handleTokenExpire(error).then(() => reloadPage());
        }
      }

      // if (
      //   error?.response?.status === 401 &&
      //   error?.response?.data?.errorCode === "AUTH_USER_NOT_FOUND" &&
      //   persistedStore.user
      // ) {
      //   if (!commonStore.appInIframe) {
      //     LocalStorage.set("lns-error-log", JSON.stringify(error.response));
      //     persistedStore.logout();
      //     reloadPage();
      //   }
      // }

      if (
        401 === error?.response?.status &&
        (error?.response?.data?.errorCode === "AUTH_TOKEN_ABSENT" ||
          error?.response?.data?.errorCode === "AUTH_USER_DEACTIVATED") &&
        persistedStore.user
      ) {
        if (commonStore.appInIframe) {
          sendMessageToParentApp({
            action: IFrameActions.AUTH_TOKEN_ABSENT,
          });
        } else {
          LocalStorage.set("lns-error-log", JSON.stringify(error.response));
          persistedStore.logout();
          reloadPage();
        }
      }

      return Promise.reject(error);
    }
  );

  identityAxiosInstance.interceptors.response.use(
    (response) => {
      return response;
    },
    (error) => {
      if (error && error.response) {
        return Promise.reject(error.response);
      }
      return Promise.reject(error);
    }
  );
  // ^ ^ ^ this will allow you to use this.$axios (for Vue Options API form)
  //       so you won't necessarily have to import axios in each vue file

  // app.config.globalProperties.$api = api
  // ^ ^ ^ this will allow you to use this.$api (for Vue Options API form)
  //       so you can easily perform requests against your app's API
});

export const setAccountInHeader = (accountId) => {
  axiosInstance.defaults.headers.common["FTX-BS"] = accountId;
};

export const setAxiosHeaders = (token, tokenType = "Bearer") => {
  axiosInstance.defaults.headers.common[
    "Authorization"
  ] = `${tokenType} ${token}`;
};

export const setidentityAxiosHeaders = (token, tokenType = "Bearer") => {
  identityAxiosInstance.defaults.headers.common[
    "Authorization"
  ] = `${tokenType} ${token}`;
};

export const setSampleAxiosUrl = () => {
  axiosInstance.defaults.baseURL = process.env.SAMPLE_API_ROOT_URI;
};

export const resetAxiosUrl = () => {
  axiosInstance.defaults.baseURL = process.env.API_ROOT_URI;
};

export const clearRequest = (config) => {
  if (config && config.url && objRequests[config.url]) {
    let pageIndex = (config.params && config.params.page) || 0;
    delete objRequests[config.url][pageIndex];
  }
};

export const setRouteName = (name) => {
  routeName = name;
};

export const resetObjRequests = () => {
  for (let key in objRequests) {
    for (let page in objRequests[key]) {
      if (objRequests[key][page].cancel) {
        objRequests[key][page].cancel();
      }
    }
  }

  objRequests = Object.assign({});
};

export const isEqualObject = (object1, object2) => {
  let arrKeys = Object.keys(object1);
  let excludeKeys = ["page", "pageSize"];

  if (arrKeys.length !== Object.keys(object2).length) {
    return false;
  }

  if (!arrKeys.length) {
    return true;
  }

  for (let key of excludeKeys) {
    let searchIndex = arrKeys.indexOf(key);

    if (searchIndex >= 0) {
      arrKeys.splice(searchIndex, 1);
    }
  }

  for (let key of arrKeys) {
    if (typeof object1[key] === "object") {
      if (object2[key] && typeof object2[key] === "object") {
        let isSame = isEqualObject(object1[key], object2[key]);
        if (!isSame) {
          return false;
        }
      } else {
        return false;
      }
    } else if (object1[key] !== object2[key]) {
      return false;
    }
  }

  return true;
};

export const createAxiosCancelSource = () => axios.CancelToken.source();

export const isCancelRequest = (error) => axios.isCancel(error);

const signinSilent = throttle(async function () {
  const persistedStore = usePersistedStore();
  let data = await oidc.signinSilent();
  setAxiosHeaders(data.access_token);
  persistedStore.addUser(data);
}, 1000);

export const setFTxCoreAccountId = (FTxCoreAccountId) => {
  eaivAxiosInstance.defaults.headers.common["FTxIdentityApiKey"] =
    FTxCoreAccountId;
};
